import { useState, useEffect } from 'react';
import { Button } from '../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Users, Search, Loader2 } from 'lucide-react';
import { BACKEND_URL } from '@/config/api';

export const StartDMModal = ({ 
  isOpen, 
  onClose, 
  token,
  onDMStarted 
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [connectedUsers, setConnectedUsers] = useState([]);
  const [isLoadingConnected, setIsLoadingConnected] = useState(false);

  // Fetch connected users when modal opens
  useEffect(() => {
    if (isOpen && token) {
      fetchConnectedUsers();
    }
  }, [isOpen, token]);

  const fetchConnectedUsers = async () => {
    setIsLoadingConnected(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/users/connected`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setConnectedUsers(data);
      }
    } catch (error) {
      console.error('Error fetching connected users:', error);
    } finally {
      setIsLoadingConnected(false);
    }
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/users/search?q=${encodeURIComponent(query)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data);
      }
    } catch (error) {
      console.error('Error searching users:', error);
    }
  };

  const handleStartDM = async (recipientId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/dm/conversations?recipient_id=${recipientId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const conversation = await response.json();
        onDMStarted?.(conversation);
        handleClose();
      }
    } catch (error) {
      console.error('Error starting DM:', error);
    }
  };

  const handleClose = () => {
    setSearchQuery('');
    setSearchResults([]);
    onClose();
  };

  // Display either search results or connected users
  const displayUsers = searchQuery.length >= 2 ? searchResults : connectedUsers;
  const showingConnected = searchQuery.length < 2;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-[#0F2942] border-white/20 text-white max-w-md">
        <DialogHeader>
          <DialogTitle>New message</DialogTitle>
          <DialogDescription className="text-white/60">
            Start a conversation with someone from your workspace or search for anyone.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="user-search" className="text-white/80">To:</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={16} />
              <Input
                id="user-search"
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Search by name or email..."
                className="bg-white/10 border-white/20 text-white placeholder:text-white/40 pl-9"
                data-testid="user-search-input"
              />
            </div>
          </div>
          
          {/* Section header */}
          {showingConnected && connectedUsers.length > 0 && (
            <div className="flex items-center gap-2 text-white/50 text-xs">
              <Users size={14} />
              <span>People in your workspace</span>
            </div>
          )}
          
          {/* Loading state */}
          {isLoadingConnected && showingConnected && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="animate-spin text-white/50" size={24} />
            </div>
          )}
          
          {/* User list */}
          {displayUsers.length > 0 && (
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {displayUsers.map(u => (
                <button
                  key={u.id}
                  onClick={() => handleStartDM(u.id)}
                  className="w-full px-3 py-2 flex items-center gap-3 hover:bg-white/10 rounded transition-colors"
                  data-testid={`dm-user-${u.id}`}
                >
                  <div 
                    className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-medium"
                    style={{ backgroundColor: u.avatar_color || '#6366F1' }}
                  >
                    {u.name?.charAt(0).toUpperCase() || 'U'}
                  </div>
                  <div className="text-left flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">{u.name}</p>
                    <p className="text-white/50 text-xs truncate">{u.email}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
          
          {/* Empty states */}
          {searchQuery.length >= 2 && searchResults.length === 0 && (
            <p className="text-white/50 text-sm text-center py-4">No users found</p>
          )}
          
          {showingConnected && !isLoadingConnected && connectedUsers.length === 0 && (
            <div className="text-center py-6">
              <Users size={32} className="mx-auto text-white/30 mb-2" />
              <p className="text-white/50 text-sm">No team members yet</p>
              <p className="text-white/40 text-xs mt-1">Invite people to your workspace to message them</p>
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={handleClose}
            className="text-white/60 hover:text-white hover:bg-white/10"
          >
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
